import { Injectable } from '@angular/core';
import { IMovement, Movement } from '../models/movement.model';
import { IMovementInsight, MovementInsight } from '../models/movement-insight.model';
import { AuthService } from './auth.service';
import { HttpClient } from '@angular/common/http';
import { SpinnerService } from './spinner.service';
import { ToastService } from './toast.service';
import { environment } from '../../environments/environment';
import { IServerResponse, ServerResponse } from '../models/server-response.model';
import { IPresignResponse } from '../models/presign-response.model';
import { IAppFile } from '../models/app-file.model';

@Injectable({
  providedIn: 'root'
})
export class MovementService {

  public loading: boolean = true;
  public movements: Movement[] = [];
  public insights?: MovementInsight;

  constructor(
    public authService: AuthService,
    private http: HttpClient,
    public spinnerService: SpinnerService,
    public toastService: ToastService
  ) { }

  /*
   ██████╗██████╗ ███████╗ █████╗ ████████╗███████╗
  ██╔════╝██╔══██╗██╔════╝██╔══██╗╚══██╔══╝██╔════╝
  ██║     ██████╔╝█████╗  ███████║   ██║   █████╗
  ██║     ██╔══██╗██╔══╝  ██╔══██║   ██║   ██╔══╝
  ╚██████╗██║  ██║███████╗██║  ██║   ██║   ███████╗
   ╚═════╝╚═╝  ╚═╝╚══════╝╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async create(
    movement: IMovement,
    idBranch: string,
  ): Promise<Movement | null> {
    let createdMovement: Movement | null = null;
    const urlApi = `${environment.urlAPI}/companies/${environment.idCompany}/branches/${idBranch}/movements`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Registrando movimiento';

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse<{ movement: IMovement }>>(
        (resolve, reject) => {
          this.http.post<IServerResponse<{ movement: IMovement }>>(
            urlApi,
            JSON.stringify(movement),
            {
              headers: {
                "content-type": "application/json",
                'Authorization': token.toString()
            }
          }).subscribe({
            next: (response: IServerResponse<{ movement: IMovement }>) => resolve(new ServerResponse<{ movement: IMovement }>(response)),
            error: (error) => {
              if (error.error) resolve(new ServerResponse(error.error));
              else {
                reject(error);
                this.toastService.show({ header: 'Error de servidor', body: `${error}`, classname: 'bg-danger text-white' });
              }
            },
          });
        }
      );

      if(response.statusCode === 201) {
        createdMovement = new Movement(response.data!.movement);
        this.toastService.show({ body: `${response.message}`, classname: 'bg-success text-white' });
      } else {
        this.toastService.show({ header: 'Error de servidor', body: `${response.message}`, classname: 'bg-danger text-white' });
      }

    } catch(error) {
      console.log(error);
      this.toastService.show({ header: 'Error', body: `${error}`, classname: 'bg-danger text-white' });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return createdMovement;
  }

  /*
  ██████╗ ███████╗██╗     ███████╗████████╗███████╗
  ██╔══██╗██╔════╝██║     ██╔════╝╚══██╔══╝██╔════╝
  ██║  ██║█████╗  ██║     █████╗     ██║   █████╗
  ██║  ██║██╔══╝  ██║     ██╔══╝     ██║   ██╔══╝
  ██████╔╝███████╗███████╗███████╗   ██║   ███████╗
  ╚═════╝ ╚══════╝╚══════╝╚══════╝   ╚═╝   ╚══════╝

  */
  async delete(
    movement: Movement
  ): Promise<boolean> {
    let flag = false;
    const urlApi = `${environment.urlAPI}/companies/${environment.idCompany}/branches/${movement.idBranch}/movements/${movement.idMovement}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Eliminando movimiento';

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.delete<IServerResponse>(urlApi, {
            headers: { 'Authorization': token.toString() }
          }).subscribe({
            next: (response: IServerResponse) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) resolve(new ServerResponse(error.error));
              else {
                reject(error);
                this.toastService.show({ header: 'Error de servidor', body: `${error}`, classname: 'bg-danger text-white' });
              }
            },
          });
        }
      );

      if(response.statusCode === 200) {
        flag = true;
        this.toastService.show({ body: `${response.message}`, classname: 'bg-success text-white' });
      } else {
        this.toastService.show({ header: 'Error de servidor', body: `${response.message}`, classname: 'bg-danger text-white' });
      }

    } catch(error) {
      console.log(error);
      this.toastService.show({ header: 'Error', body: `${error}`, classname: 'bg-danger text-white' });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';
  
    return flag;
  }

  /*
   ██████╗ ███████╗████████╗
  ██╔════╝ ██╔════╝╚══██╔══╝
  ██║  ███╗█████╗     ██║
  ██║   ██║██╔══╝     ██║
  ╚██████╔╝███████╗   ██║
   ╚═════╝ ╚══════╝   ╚═╝

  */
  async get(
    idMovement: string,
    idBranch: string,
  ): Promise<Movement | null> {
    let movement: Movement | null = null;
    const urlApi = `${environment.urlAPI}/companies/${environment.idCompany}/branches/${idBranch}/movements/${idMovement}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Obteniendo movimiento';

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse<{ movement: IMovement }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ movement: IMovement }>>(urlApi, {
            headers: { "content-type": "application/json", "Authorization": token.toString() }
          }).subscribe({
            next: (response: IServerResponse<{ movement: IMovement }>) => resolve(new ServerResponse<{ movement: IMovement }>(response)),
            error: (error) => {
              if (error.error) resolve(new ServerResponse(error.error));
              else reject(error);
            },
          });
        }
      );

      if(response.statusCode === 200) {
        movement = new Movement(response.data!.movement);
      } else {
        this.toastService.show({ header: 'Error de servidor', body: `${response.message}`, classname: 'bg-danger text-white' });
      }

    } catch(error) {
      console.log(error);
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return movement;
  }

  /*
   ██████╗ ███████╗████████╗ █████╗ ██╗     ██╗
  ██╔════╝ ██╔════╝╚══██╔══╝██╔══██╗██║     ██║
  ██║  ███╗█████╗     ██║   ███████║██║     ██║
  ██║   ██║██╔══╝     ██║   ██╔══██║██║     ██║
  ╚██████╔╝███████╗   ██║   ██║  ██║███████╗███████╗
   ╚═════╝ ╚══════╝   ╚═╝   ╚═╝  ╚═╝╚══════╝╚══════╝

  */
  async getAll(
    idBranch: string,
    month: number,
    year: number
  ) {
    this.loading = true;
    const urlApi = `${environment.urlAPI}/companies/${environment.idCompany}/branches/${idBranch}/movements?month=${month}&year=${year}`;

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse<{ movements: IMovement[], insights: IMovementInsight }>>(
        (resolve, reject) => {
          this.http.get<IServerResponse<{ movements: IMovement[], insights: IMovementInsight }>>(urlApi, {
            headers: { "content-type": "application/json", "Authorization": token.toString() }
          }).subscribe({
            next: (response: IServerResponse<{ movements: IMovement[], insights: IMovementInsight }>) => resolve(new ServerResponse<{ movements: IMovement[], insights: IMovementInsight }>(response)),
            error: (error) => {
              if (error.error) resolve(new ServerResponse(error.error));
              else reject(error);
            },
          });
        }
      );

      if(response.statusCode === 200) {
        this.movements = response.data!.movements.map(
          movement => new Movement(movement)
        );

        this.insights = new MovementInsight(response.data!.insights);

      } else {
        this.toastService.show({ header: 'Error de servidor', body: `${response.message}`, classname: 'bg-danger text-white' });
      }

    } catch(error) {
      console.log(error);
    }

    this.loading = false;
  }

  /*
  ██╗   ██╗██████╗ ██████╗  █████╗ ████████╗███████╗
  ██║   ██║██╔══██╗██╔══██╗██╔══██╗╚══██╔══╝██╔════╝
  ██║   ██║██████╔╝██║  ██║███████║   ██║   █████╗
  ██║   ██║██╔═══╝ ██║  ██║██╔══██║   ██║   ██╔══╝
  ╚██████╔╝██║     ██████╔╝██║  ██║   ██║   ███████╗
   ╚═════╝ ╚═╝     ╚═════╝ ╚═╝  ╚═╝   ╚═╝   ╚══════╝

  */
  async update(
    movement: IMovement
  ): Promise<boolean> {
    let flag = false;
    const urlApi = `${environment.urlAPI}/companies/${environment.idCompany}/branches/${movement.idBranch}/movements/${movement.idMovement}`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Actualizando movimiento';

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.put<IServerResponse>(urlApi, JSON.stringify(movement), {
            headers: { "content-type": "application/json", 'Authorization': token.toString() }
          }).subscribe({
            next: (response: IServerResponse) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) resolve(new ServerResponse(error.error));
              else {
                reject(error);
                this.toastService.show({ header: 'Error de servidor', body: `${error}`, classname: 'bg-danger text-white' });
              }
            },
          });
        }
      );

      if(response.statusCode === 200) {
        flag = true;
        this.toastService.show({ body: `${response.message}`, classname: 'bg-success text-white' });
      } else {
        this.toastService.show({ header: 'Error de servidor', body: `${response.message}`, classname: 'bg-danger text-white' });
      }

    } catch(error) {
      console.log(error);
      this.toastService.show({ header: 'Error', body: `${error}`, classname: 'bg-danger text-white' });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return flag;
  }

  /*
  ██╗   ██╗██████╗ ██╗      ██████╗  █████╗ ██████╗     ████████╗██╗ ██████╗██╗  ██╗███████╗████████╗
  ██║   ██║██╔══██╗██║     ██╔═══██╗██╔══██╗██╔══██╗    ╚══██╔══╝██║██╔════╝██║ ██╔╝██╔════╝╚══██╔══╝
  ██║   ██║██████╔╝██║     ██║   ██║███████║██║  ██║       ██║   ██║██║     █████╔╝ █████╗     ██║
  ██║   ██║██╔═══╝ ██║     ██║   ██║██╔══██║██║  ██║       ██║   ██║██║     ██╔═██╗ ██╔══╝     ██║
  ╚██████╔╝██║     ███████╗╚██████╔╝██║  ██║██████╔╝       ██║   ██║╚██████╗██║  ██╗███████╗   ██║
   ╚═════╝ ╚═╝     ╚══════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝        ╚═╝   ╚═╝ ╚═════╝╚═╝  ╚═╝╚══════╝   ╚═╝

  */
  // region uploadTicket
  async uploadTicket(
    idMovement: string,
    idBranch: string,
    contentType: string
  ): Promise<IPresignResponse | null> {
    let presignFile: IPresignResponse | null = null;
    const urlApi = `${environment.urlAPI}/companies/${environment.idCompany}/branches/${idBranch}/movements/${idMovement}/ticket`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Generando URL para ticket...';

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.post<IServerResponse>(
            urlApi,
            JSON.stringify({ contentType }),
            {
              headers: {
                "content-type": "application/json",
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (response: IServerResponse) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) resolve(new ServerResponse(error.error));
              else {
                reject(error);
                this.toastService.show({ header: 'Error de servidor', body: `${error}`, classname: 'bg-danger text-white' });
              }
            },
          });
        }
      );

      if (response.statusCode === 200 || response.statusCode === 201) {
        presignFile = response.data?.presignFile || null;
      } else {
        this.toastService.show({ header: 'Error de servidor', body: `${response.message}`, classname: 'bg-danger text-white' });
      }

    } catch (error) {
      console.log(error);
      this.toastService.show({ header: 'Error', body: `${error}`, classname: 'bg-danger text-white' });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return presignFile;
  }

  /*
  ██╗   ██╗██████╗ ██╗      ██████╗  █████╗ ██████╗     ██████╗ ██████╗ ███████╗
  ██║   ██║██╔══██╗██║     ██╔═══██╗██╔══██╗██╔══██╗    ██╔══██╗██╔══██╗██╔════╝
  ██║   ██║██████╔╝██║     ██║   ██║███████║██║  ██║    ██████╔╝██║  ██║█████╗
  ██║   ██║██╔═══╝ ██║     ██║   ██║██╔══██║██║  ██║    ██╔═══╝ ██║  ██║██╔══╝
  ╚██████╔╝██║     ███████╗╚██████╔╝██║  ██║██████╔╝    ██║     ██████╔╝██║
   ╚═════╝ ╚═╝     ╚══════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝     ╚═╝     ╚═════╝ ╚═╝

  */
  // region uploadInvoicePdf
  async uploadInvoicePdf(
    idMovement: string,
    idBranch: string,
    contentType: string
  ): Promise<IPresignResponse | null> {
    let presignFile: IPresignResponse | null = null;
    const urlApi = `${environment.urlAPI}/companies/${environment.idCompany}/branches/${idBranch}/movements/${idMovement}/invoice-pdf`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Generando URL para factura PDF...';

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.post<IServerResponse>(
            urlApi,
            JSON.stringify({ contentType }),
            {
              headers: {
                "content-type": "application/json",
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (response: IServerResponse) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) resolve(new ServerResponse(error.error));
              else {
                reject(error);
                this.toastService.show({ header: 'Error de servidor', body: `${error}`, classname: 'bg-danger text-white' });
              }
            },
          });
        }
      );

      if (response.statusCode === 200 || response.statusCode === 201) {
        presignFile = response.data?.presignFile || null;
      } else {
        this.toastService.show({ header: 'Error de servidor', body: `${response.message}`, classname: 'bg-danger text-white' });
      }

    } catch (error) {
      console.log(error);
      this.toastService.show({ header: 'Error', body: `${error}`, classname: 'bg-danger text-white' });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return presignFile;
  }

  /*
  ██╗   ██╗██████╗ ██╗      ██████╗  █████╗ ██████╗     ██╗  ██╗███╗   ███╗██╗
  ██║   ██║██╔══██╗██║     ██╔═══██╗██╔══██╗██╔══██╗    ╚██╗██╔╝████╗ ████║██║
  ██║   ██║██████╔╝██║     ██║   ██║███████║██║  ██║     ╚███╔╝ ██╔████╔██║██║
  ██║   ██║██╔═══╝ ██║     ██║   ██║██╔══██║██║  ██║     ██╔██╗ ██║╚██╔╝██║██║
  ╚██████╔╝██║     ███████╗╚██████╔╝██║  ██║██████╔╝    ██╔╝ ██╗██║ ╚═╝ ██║███████╗
   ╚═════╝ ╚═╝     ╚══════╝ ╚═════╝ ╚═╝  ╚═╝╚═════╝     ╚═╝  ╚═╝╚═╝     ╚═╝╚══════╝

  */
  // region uploadInvoiceXml
  async uploadInvoiceXml(
    idMovement: string,
    idBranch: string,
    contentType: string
  ): Promise<IPresignResponse | null> {
    let presignFile: IPresignResponse | null = null;
    const urlApi = `${environment.urlAPI}/companies/${environment.idCompany}/branches/${idBranch}/movements/${idMovement}/invoice-xml`;

    this.spinnerService.loading = true;
    this.spinnerService.message = 'Generando URL para factura XML...';

    try {
      const token = await this.authService.getToken();
      const response = await new Promise<ServerResponse>(
        (resolve, reject) => {
          this.http.post<IServerResponse>(
            urlApi,
            JSON.stringify({ contentType }),
            {
              headers: {
                "content-type": "application/json",
                'Authorization': token.toString()
              }
            }
          ).subscribe({
            next: (response: IServerResponse) => resolve(new ServerResponse(response)),
            error: (error) => {
              if (error.error) resolve(new ServerResponse(error.error));
              else {
                reject(error);
                this.toastService.show({ header: 'Error de servidor', body: `${error}`, classname: 'bg-danger text-white' });
              }
            },
          });
        }
      );

      if (response.statusCode === 200 || response.statusCode === 201) {
        presignFile = response.data?.presignFile || null;
      } else {
        this.toastService.show({ header: 'Error de servidor', body: `${response.message}`, classname: 'bg-danger text-white' });
      }

    } catch (error) {
      console.log(error);
      this.toastService.show({ header: 'Error', body: `${error}`, classname: 'bg-danger text-white' });
    }

    this.spinnerService.loading = false;
    this.spinnerService.message = '';

    return presignFile;
  }

  /*
  ██████╗ ███████╗██╗     ███████╗████████╗███████╗    ██╗███╗   ███╗ █████╗  ██████╗ ███████╗
  ██╔══██╗██╔════╝██║     ██╔════╝╚══██╔══╝██╔════╝    ██║████╗ ████║██╔══██╗██╔════╝ ██╔════╝
  ██║  ██║█████╗  ██║     █████╗     ██║   █████╗      ██║██╔████╔██║███████║██║  ███╗█████╗
  ██║  ██║██╔══╝  ██║     ██╔══╝     ██║   ██╔══╝      ██║██║╚██╔╝██║██╔══██║██║   ██║██╔══╝
  ██████╔╝███████╗███████╗███████╗   ██║   ███████╗    ██║██║ ╚═╝ ██║██║  ██║╚██████╔╝███████╗
  ╚═════╝ ╚══════╝╚══════╝╚══════╝   ╚═╝   ╚══════╝    ╚═╝╚═╝     ╚═╝╚═╝  ╚═╝ ╚═════╝ ╚══════╝

  */
  // region deleteImage
  /**
   * Elimina una imagen/archivo huérfano en caso de fallo del PUT a S3.
   * DELETE /v1/files/{idImage}
   */
  async deleteImage(
    idImage: string
  ): Promise<void> {
    const urlApi = `${environment.urlAPI}/files/${idImage}`;

    try {
      const token = await this.authService.getToken();
      await new Promise<void>((resolve) => {
        this.http.delete<IServerResponse>(
          urlApi,
          { headers: { 'Authorization': token.toString() } }
        ).subscribe({
          next: () => resolve(),
          error: (error) => {
            console.error('[MovementService] deleteImage falló:', error);
            resolve();
          },
        });
      });
    } catch (error) {
      console.error('[MovementService] deleteImage excepción:', error);
    }
  }

}
